// Grids & Gantts — cloning work items, with their tree.
//
// A structure page is where a reader looks at a plan, and a plan is mostly
// made of things that have been done before: the same epic, the same
// modules under it, the same tasks under those, for the next order. Making
// that by hand is forty creates and forty links, and the one that gets
// forgotten is the link. So the structure clones: one work item, or the
// whole architectural tree under it, as many times as asked, with every
// field carried across except the ones the reader chose to clear, and every
// tree connection remade between the copies.
//
// THREE PURE PIECES AND ONE RUNNER, so the parts that can be wrong are
// checked under node without a server:
//
//   cloneFieldsFor   what the CREATE is sent for one item — its fields, in
//                    the shape the create endpoint wants, minus the ones the
//                    reader cleared, with the ones the reader gave a new
//                    value to, and without the ones that cannot be set at all
//   clearableFields  the fields a reader may clear or change: everything a
//                    copy would CARRY — the create screens' fields AND the
//                    fields the originals hold that a copy carries anyway
//   anchorPlan       where a copied ROOT hangs: off the original's own
//                    parents, off a parent the reader picked along the
//                    hierarchy, or nowhere
//   planClone        the order the copies are made in, parents before
//                    children, and HOW each child hangs off its parent (the
//                    Parent field, or a link of a named type in a named
//                    direction) — read off the originals, so the copies are
//                    joined exactly the way the originals are
//   linksWithin      the links AMONG the tree's items that are not the
//                    tree's own edges (a "blocks" between two tasks under
//                    the same epic), remade between the copies afterwards
//   runClone         the calls, in that order, N times, reporting as it goes
//
// A NEW WORK ITEM STARTS AT THE START OF ITS WORKFLOW. That is what a create
// does, and it is the one thing deliberately NOT carried across: the status,
// the resolution, the dates the server stamps. Everything the reader can
// see on the create screen is copied; everything the server owns is not.
//
// A COPY CAN GO SOMEWHERE ELSE. The reader may give the copies a different
// parent — one of the work items on the page whose type may hold theirs,
// by the site's own creation tree — or a different project, in which case a
// parent in that project is required, so the copies land where the page
// can show them rather than nowhere. And the reader may CHANGE fields, per
// work item, as well as clear them: a new order number on every copy, a
// different engineer on one of them.

import { keyOf, fieldsOf, parentKeyOf } from './structure';

// Fields the create endpoint owns or refuses, whatever the screen says.
export const NEVER_CLONED = new Set([
  'project', 'issuetype', 'parent', 'key', 'id', 'self', 'status', 'resolution',
  'created', 'updated', 'resolutiondate', 'lastViewed', 'reporter', 'creator',
  'issuelinks', 'subtasks', 'attachment', 'comment', 'worklog', 'watches', 'votes',
  'progress', 'aggregateprogress', 'timespent', 'aggregatetimespent', 'workratio',
  'statuscategorychangedate', 'issuekey', 'thumbnail', 'security',
]);

const idOf = (v) => (v && v.id != null ? { id: String(v.id) } : null);
const userOf = (v) => (v && v.accountId ? { accountId: v.accountId } : null);

// WHAT A LIST FIELD HOLDS, when its definition does not say. The create
// screen describes a multi-select as `type: 'array'` and leaves `items` out —
// so "Responsible Engineer:" (a multi-select of people's names) was treated
// as a list of nothing in particular: the clone dialog offered a plain text
// box for it, and the words typed there went to the server as bare strings,
// which an option field does not store. The field's own type (`custom`) says
// what it holds, and failing that so do its allowed values.
const CUSTOM_ITEMS = [
  [/:(multiselect|multicheckboxes)$/, 'option'],
  [/:multiuserpicker$/, 'user'],
  [/:labels$/, 'string'],
  [/:multiversion$/, 'version'],
  [/:multigrouppicker$/, 'group'],
];
const SYSTEM_ITEMS = { labels: 'string', components: 'component', fixVersions: 'version', versions: 'version' };
export function itemsOf(def) {
  const d = def || {};
  const s = d.schema && typeof d.schema === 'object' ? d.schema : d;
  if (s.items) return String(s.items);
  const custom = String(s.custom || d.custom || '');
  const hit = CUSTOM_ITEMS.find(([re]) => re.test(custom));
  if (hit) return hit[1];
  const sys = String(s.system || d.system || '');
  if (SYSTEM_ITEMS[sys]) return SYSTEM_ITEMS[sys];
  const first = (Array.isArray(d.allowedValues) ? d.allowedValues : []).find((v) => v && typeof v === 'object');
  if (first) return first.accountId ? 'user' : 'option';
  return '';
}

// An option, in the create's shape. An option named only by its words — a
// value typed rather than picked — goes by value, and the server finds its
// id. When the field's allowed values are known and the original's option
// is not among them (another project's context), it is found by its label.
function optionOf(x, allowed) {
  if (x == null || x === '') return null;
  const label = typeof x === 'object' ? (x.value != null ? x.value : x.name) : x;
  if (allowed && allowed.length) {
    if (typeof x === 'object' && x.id != null && allowed.some((a) => a && String(a.id) === String(x.id))) return { id: String(x.id) };
    const want = String(label == null ? '' : label).trim().toLowerCase();
    const same = want ? allowed.find((a) => a && String(a.value != null ? a.value : (a.name || '')).trim().toLowerCase() === want) : null;
    if (same && same.id != null) return { id: String(same.id) };
  }
  if (typeof x === 'object') return idOf(x) || (label != null ? { value: String(label) } : null);
  return { value: String(x) };
}

// One value, in the shape the create endpoint wants for its schema. Null
// means "send nothing" — an empty field is not cloned as an empty field, it
// is simply left out, which is what a fresh create would do.
export function createValue(schema, v) {
  if (v == null || v === '') return null;
  const t = (schema && schema.type) || '';
  const items = t === 'array' ? itemsOf(schema) : '';
  const sys = (schema && schema.system) || '';
  const allowed = schema && Array.isArray(schema.allowedValues) ? schema.allowedValues : null;
  if (t === 'user' || sys === 'assignee') return userOf(v);
  if (t === 'option') return optionOf(v, allowed);
  if (t === 'priority' || sys === 'priority' || t === 'version' || t === 'component' || t === 'securitylevel') return idOf(v) || (v.value != null ? { value: v.value } : null);
  if (t === 'array') {
    const list = Array.isArray(v) ? v : [v];
    if (items === 'user') return list.map(userOf).filter(Boolean);
    if (items === 'option') return list.map((x) => optionOf(x, allowed)).filter(Boolean);
    if (items === 'version' || items === 'component') return list.map((x) => idOf(x) || (x && x.value != null ? { value: x.value } : null)).filter(Boolean);
    if (items === 'string') return list.map((x) => (typeof x === 'object' && x ? (x.value || x.name || '') : String(x))).filter(Boolean);
    return list.map((x) => (x && typeof x === 'object' ? (idOf(x) || userOf(x) || x) : x));
  }
  if (t === 'number') { const n = Number(v); return Number.isFinite(n) ? n : null; }
  if (t === 'date') return String(v).slice(0, 10);
  if (t === 'datetime' || t === 'string' || t === 'any') return v;
  if (typeof v === 'object') return idOf(v) || userOf(v) || v;
  return v;
}

// The system fields a create accepts and a clone should carry. Everything
// else the server owns is in NEVER_CLONED; custom fields are carried by
// their own rule below.
export const SYSTEM_CARRIED = ['summary', 'description', 'priority', 'assignee', 'labels', 'duedate', 'components', 'fixVersions', 'versions', 'environment'];

// What the create is sent for one item. `meta` is the create screen for its
// project and work type (`api.getCreateMeta`): fieldId → { name, type, items,
// system, custom, required } — it says the SHAPE each value wants.
//
// EVERY FIELD THE ORIGINAL HOLDS IS CARRIED, not only the ones the create
// screen lists. The first live run refused every copy with "'Responsible
// Engineer' must be filled in" while the originals all had one: the clone
// was sending only what the create screen named, and a validator can demand
// a field the screen does not show. The create endpoint takes any custom
// field, so a clone sends the lot — the reader chose what to clear, and
// the server's own fields are the only ones left behind.
//
// `set` is the reader's NEW VALUES for this one item — fieldId → value in the
// READ shape (the option with its id, the person with their accountId, the
// date as a day) — laid over the original before the shape is decided, so a
// changed field is sent exactly as a carried one is. An empty new value
// changes nothing: clearing is a separate, deliberate tick.
const isBlank = (v) => v == null || v === '' || (Array.isArray(v) && !v.length);
export const carriedIds = (src, meta) => {
  const ids = new Set(Object.keys(meta || {}));
  Object.keys(src || {}).forEach((k) => { if (/^customfield_/.test(k) || SYSTEM_CARRIED.includes(k)) ids.add(k); });
  return ids;
};
export function cloneFieldsFor(original, meta, { clear = [], summary = null, set = null } = {}) {
  const src = { ...fieldsOf(original) };
  Object.keys(set || {}).forEach((k) => { if (!isBlank(set[k])) src[k] = set[k]; });
  const cleared = new Set((clear || []).map(String));
  const ids = carriedIds(src, meta);
  Object.keys(set || {}).forEach((k) => { if (!isBlank(set[k])) ids.add(k); });
  const out = {};
  ids.forEach((fieldId) => {
    if (NEVER_CLONED.has(fieldId) || cleared.has(fieldId)) return;
    const v = createValue((meta || {})[fieldId] || null, src[fieldId]);
    if (v == null || (Array.isArray(v) && !v.length)) return;
    out[fieldId] = v;
  });
  if (summary != null) out.summary = summary;
  else if (out.summary == null && src.summary) out.summary = src.summary;
  return out;
}

// The fields the reader may choose to clear or change: EVERYTHING A COPY
// WOULD CARRY. That is the create screens' fields — with the work types
// that carry each one, so the list can say "(Epic, Task)" — and, since
// cloneFieldsFor carries every custom field the original holds whether a
// screen lists it or not, every such field the originals actually hold.
// The first cut listed only the screens, so a field filled in later in the
// work's life ("Actual Time", stamped when the job is done) was carried
// onto every copy and could not be cleared, because it was never offered.
// A field off every screen is named from the site's field list and marked
// `onScreen: false` so the dialog can say where it comes from.
//
// The summary is offered to CHANGE but never to clear — a create without
// one is refused — and is listed first. `also` names fields (by name) to
// list whether or not anything holds them — the admin's short list, which
// should read "0 of 20" rather than vanish.
const normName = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
// A create screen's field definition as a schema — what each list holds
// filled in (itemsOf), because the screen leaves it out.
export const schemaOf = (def) => (def ? { type: def.type || '', items: def.type === 'array' ? itemsOf(def) : (def.items || ''), system: def.system || '', custom: def.custom || '' } : null);
// A field the site lists carries a type but not what its array holds; the
// value the original holds says that well enough.
export function inferSchema(def, value) {
  const base = schemaOf(def && def.schema ? { type: def.schema.type, custom: def.schema.custom } : null) || { type: '', items: '', system: '', custom: '' };
  const first = Array.isArray(value) ? value[0] : value;
  if (base.type === 'array' || Array.isArray(value)) {
    base.type = 'array';
    if (!base.items) base.items = first && typeof first === 'object' ? (first.accountId ? 'user' : 'option') : 'string';
  } else if (!base.type) {
    base.type = first && typeof first === 'object' ? (first.accountId ? 'user' : 'option') : (typeof first === 'number' ? 'number' : 'string');
  }
  return base;
}
export function clearableFields(metas, { items = [], fieldList = [], also = [] } = {}) {
  const by = new Map();
  const fl = Array.isArray(fieldList) ? fieldList : [];
  const listed = (fieldId) => fl.find((f) => f && (f.id === fieldId || f.key === fieldId)) || null;
  (metas || []).forEach(({ typeName, meta }) => {
    Object.keys(meta || {}).forEach((fieldId) => {
      if (NEVER_CLONED.has(fieldId)) return;
      const def = meta[fieldId] || {};
      const cur = by.get(fieldId) || {
        fieldId, name: def.name || (listed(fieldId) || {}).name || fieldId, types: [], onScreen: true,
        schema: schemaOf(def), allowedValues: Array.isArray(def.allowedValues) && def.allowedValues.length ? def.allowedValues : null,
        noClear: fieldId === 'summary',
      };
      if (!cur.allowedValues && Array.isArray(def.allowedValues) && def.allowedValues.length) cur.allowedValues = def.allowedValues;
      if (typeName && !cur.types.includes(typeName)) cur.types.push(typeName);
      by.set(fieldId, cur);
    });
  });
  (items || []).forEach((it) => {
    const f = fieldsOf(it);
    Object.keys(f).forEach((fieldId) => {
      if (by.has(fieldId) || NEVER_CLONED.has(fieldId) || fieldId === 'summary') return;
      if (!(/^customfield_/.test(fieldId) || SYSTEM_CARRIED.includes(fieldId))) return;
      if (isBlank(f[fieldId])) return;
      const def = listed(fieldId);
      by.set(fieldId, { fieldId, name: (def && def.name) || fieldId, types: [], onScreen: false, schema: inferSchema(def, f[fieldId]), allowedValues: null, noClear: false });
    });
  });
  (also || []).forEach((name) => {
    const want = normName(name);
    const def = want ? fl.find((f) => f && normName(f.name) === want) : null;
    const fieldId = def && (def.id || def.key);
    if (!fieldId || by.has(fieldId) || NEVER_CLONED.has(fieldId) || fieldId === 'summary') return;
    if (!(/^customfield_/.test(fieldId) || SYSTEM_CARRIED.includes(fieldId))) return;
    by.set(fieldId, { fieldId, name: def.name, types: [], onScreen: false, schema: inferSchema(def, null), allowedValues: null, noClear: false });
  });
  return [...by.values()].sort((a, b) => (a.fieldId === 'summary' ? -1 : (b.fieldId === 'summary' ? 1 : String(a.name).localeCompare(String(b.name)))));
}

// The admin's short list — "the fields worth a second look before a copy
// is made" — named by field NAME in the page's settings, matched the way
// every other name on a structure is (case and punctuation ignored), and
// only the ones this clone actually carries. Everything else follows.
export function groupClearable(fields, attention) {
  const want = new Set((attention || []).map(normName).filter(Boolean));
  const important = [];
  const other = [];
  (fields || []).forEach((f) => { (want.has(normName(f.name)) ? important : other).push(f); });
  return { important, other };
}

// ---- where a copied root hangs -------------------------------------------------
// The creation tree (the architectural hierarchy) words an edge from the
// CHILD's side: a real child, or a link of a named type in a named
// direction — `linkDirection` is the phrase the child displays, and the
// create puts the outward-phrase item in `inwardIssue` (see
// ./hierarchy.js, linkNew). A clone edge says the same thing the way the
// runner joins: which end the child is.
export const isChildEdge = (edge) => {
  const e = edge || {};
  return e.relationshipType === 'subtask' || e.relationshipType === 'child' || (!e.relationshipType && !!e.subtask);
};
export function cloneEdgeOf(edge, defaultLinkName = 'Link') {
  const e = edge || {};
  if (isChildEdge(e)) return { kind: 'parent' };
  return {
    kind: 'link',
    type: { id: e.linkTypeId ? String(e.linkTypeId) : '', name: e.linkTypeName || defaultLinkName },
    childIs: e.linkDirection === 'inward' ? 'outward' : 'inward',
  };
}

// The originals' own parents, on the page, with the edge each one holds
// the root by — what "the same place as the original" means. `parentsOf`
// answers the keys that list this one as a child. A parent the data does
// not say how it holds the child is left out: nothing can be joined to it.
export function sameAnchors(rootKey, byKey, parentsOf) {
  const root = byKey.get(rootKey);
  const out = [];
  (parentsOf(rootKey) || []).forEach((pk) => {
    const parent = byKey.get(pk);
    const edge = edgeBetween(parent, root);
    if (edge) out.push({ key: pk, edge });
  });
  return out;
}

// The plan with every ROOT told where to hang: `anchorsOf(rootKey)` answers
// [{ key, edge }] — the original's own parents, one the reader picked, or
// none. Children keep hanging off their copied parents.
export function anchorPlan(plan, anchorsOf) {
  return (plan || []).map((n) => (n.parentKey ? n : { ...n, anchors: (typeof anchorsOf === 'function' ? anchorsOf(n.key) : anchorsOf) || [] }));
}

// ---- a new parent, from ALL OF STRIDE ------------------------------------------
// A clone is made in Stride, not in a grid, so the parent it is given is any
// work item the target project's creation tree allows — not only the rows
// this grid happened to load. (It used to be only those: cloning an epic into
// another project said "0 work items may hold Epic work" while that project
// had modules that could, because the grid never loaded them.)
//
// holdingTypes: which of a project's work types may hold EVERY one of the
// child types being cloned, and by what edge each child hangs. `allowedOf(t)`
// is the creation tree's own answer for type t ([{ issueTypeName, … }]).
const lc = (s) => String(s || '').trim().toLowerCase();
export function holdingTypes(typeNames, allowedOf, childTypeNames, linkName = 'Link') {
  const want = [...new Set((childTypeNames || []).map(lc).filter(Boolean))];
  if (!want.length) return [];
  const out = [];
  [...new Set((typeNames || []).filter(Boolean))].forEach((tn) => {
    const allowed = new Map((allowedOf(tn) || []).map((e) => [lc(e.issueTypeName), cloneEdgeOf(e, linkName)]));
    if (!want.every((w) => allowed.has(w))) return;
    const edgeByType = {};
    want.forEach((w) => { edgeByType[w] = allowed.get(w); });
    out.push({ typeName: tn, edgeByType });
  });
  return out;
}

// The query for them: the project, those work types, and — when the reader
// typed something — Zebra Search's own key-or-summary match.
const zqlStr = (s) => `"${String(s == null ? '' : s).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
export function parentSearchZql(projectKey, typeNames, text = '') {
  const t = String(text || '').trim();
  const types = [...new Set((typeNames || []).filter(Boolean))].map(zqlStr).join(', ');
  return `project = ${zqlStr(projectKey)} AND issuetype in (${types})${t ? ` AND (key ~ ${zqlStr(t)} OR text ~ ${zqlStr(t)})` : ''}`;
}

// THE PROJECT, FOUND BY WHAT IS TYPED. A site has hundreds of projects; a
// project matches when EVERY word typed is in its key or its name (so
// "seatback 2026" finds "JSUTP26 — JS2/5 Seatback Upgrade TP - Production
// 2026"), and the closest keys come first: the key itself, a key starting
// with the first word, a key containing it, then a match in the name.
export function rankProjects(projects, q) {
  const words = String(q || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
  const all = (projects || []).filter((p) => p && p.key);
  const byKey = (a, b) => String(a.key).localeCompare(String(b.key));
  if (!words.length) return all.slice().sort(byKey);
  const scored = [];
  all.forEach((p) => {
    const key = String(p.key).toLowerCase();
    const hay = `${key} ${String(p.name || '').toLowerCase()}`;
    if (!words.every((w) => hay.includes(w))) return;
    const w = words[0];
    scored.push({ p, score: key === w ? 0 : (key.startsWith(w) ? 1 : (key.includes(w) ? 2 : 3)) });
  });
  return scored.sort((a, b) => a.score - b.score || byKey(a.p, b.p)).map((s) => s.p);
}

// Work item keys the way a person reads them: by project, then by number.
export const byKeyOrder = (a, b) => {
  const [pa, na] = String((a && a.key) || a || '').split('-');
  const [pb, nb] = String((b && b.key) || b || '').split('-');
  return pa === pb ? (Number(na) || 0) - (Number(nb) || 0) : String(pa).localeCompare(String(pb));
};

// ---- the plan ----------------------------------------------------------------
// How a child hangs off its parent, read off the ORIGINALS. A child whose
// Parent field names the parent is a real child; otherwise the parent's own
// links are searched for the one that reaches the child, and that link's
// type and direction are what the copy will be joined with. `null` means the
// tree knows they are related but the data does not say how — the copy is
// still made, and reported as unjoined rather than silently orphaned.
export function edgeBetween(parent, child) {
  if (!parent || !child) return null;
  if (parentKeyOf(child) === keyOf(parent)) return { kind: 'parent' };
  for (const l of (fieldsOf(parent).issuelinks || [])) {
    const t = l && l.type;
    if (!t) continue;
    if (l.inwardIssue && l.inwardIssue.key === keyOf(child)) return { kind: 'link', type: { id: t.id, name: t.name }, childIs: 'inward' };
    if (l.outwardIssue && l.outwardIssue.key === keyOf(child)) return { kind: 'link', type: { id: t.id, name: t.name }, childIs: 'outward' };
  }
  return null;
}

// The order the copies are made in: every root, then its children, depth
// first, parents always before children. `include` is the set of keys the
// reader kept ticked; a node left out takes its whole branch with it, and
// in 'single' mode nothing below a root is taken at all. A cycle is walked
// once.
export function planClone({ roots, byKey, kidsOf, include = null, mode = 'tree' }) {
  const plan = [];
  const seen = new Set();
  const keep = (k) => !include || include.has(k);
  const walk = (key, parentKey, onPath) => {
    if (seen.has(key) || onPath.has(key)) return;
    const it = byKey.get(key);
    if (!it) return;
    seen.add(key);
    const parent = parentKey ? byKey.get(parentKey) : null;
    plan.push({ key, parentKey: parentKey || null, edge: parent ? edgeBetween(parent, it) : null });
    if (mode !== 'tree') return;
    onPath.add(key);
    (kidsOf.get(key) || []).forEach((ck) => { if (keep(ck)) walk(ck, key, onPath); });
    onPath.delete(key);
  };
  (roots || []).forEach((k) => { if (keep(k)) walk(k, null, new Set()); });
  return plan;
}

// Links AMONG the planned items that are not tree edges — remade between
// the copies once every copy exists. Each original link once, whichever end
// it was read from.
export function linksWithin(plan, byKey) {
  const inPlan = new Set(plan.map((n) => n.key));
  const edges = new Set(plan.filter((n) => n.edge && n.edge.kind === 'link').map((n) => `${n.parentKey}>${n.key}`));
  const seen = new Set();
  const out = [];
  plan.forEach((n) => {
    (fieldsOf(byKey.get(n.key)).issuelinks || []).forEach((l) => {
      const t = l && l.type;
      if (!t) return;
      const inward = l.inwardIssue && l.inwardIssue.key;
      const outward = l.outwardIssue && l.outwardIssue.key;
      const other = inward || outward;
      if (!other || !inPlan.has(other)) return;
      // The link as the server sees it: outward end → inward end.
      const from = inward ? n.key : other;
      const to = inward ? other : n.key;
      const id = l.id != null ? String(l.id) : `${t.name}|${from}|${to}`;
      if (seen.has(id)) return;
      if (edges.has(`${from}>${to}`) || edges.has(`${to}>${from}`)) return;
      seen.add(id);
      out.push({ type: { id: t.id, name: t.name }, from, to });
    });
  });
  return out;
}

const linkPayload = (type, inwardKey, outwardKey) => ({
  type: type.id ? { id: String(type.id), name: type.name } : { name: type.name },
  inwardIssue: { key: inwardKey },
  outwardIssue: { key: outwardKey },
});

// ---- the runner ---------------------------------------------------------------
// One copy at a time, one item at a time, parents first. A refusal on one
// item is recorded and its branch is skipped — a child cannot be joined to a
// parent that does not exist — and the rest of the run carries on. The
// result says, per copy, which original became which key, and what failed.
//
// THE ORIGINALS ARE READ IN FULL FIRST. The page holds each work item with
// only the fields its columns asked for; a clone made from that copy would
// carry a summary, two dates and whatever else happened to be on screen, and
// nothing the validators want. `loadFull(keys)` answers every field of every
// planned item, and that is what the copies are made from.
//
// `edits` is key → { fieldId: value }, the reader's new values per original.
// `project` is a project key the copies are made in instead of their
// originals', and `typeIn(projectKey, typeName)` says whether that project
// offers the work type (answering it, or null): a copy of a type the
// project does not have is refused here, by name, rather than by a 400.
// A root's `anchors` (see anchorPlan) are joined right after it is made.
export async function runClone({
  api, plan, byKey, copies = 1, clear = [], edits = null, project = null, typeIn = null,
  metaOf, onProgress = () => {}, summaryOf = null, loadFull = null,
}) {
  const total = Math.max(0, copies) * plan.length;
  let done = 0;
  const created = [];   // { copy, from, key }
  const errors = [];    // { copy, from, error }
  if (typeof loadFull === 'function') {
    const full = await loadFull(plan.map((n) => n.key));
    const merged = new Map(byKey);
    (full || new Map()).forEach((it, k) => { if (it) merged.set(k, { ...(merged.get(k) || {}), ...it, fields: { ...((merged.get(k) || {}).fields || {}), ...(it.fields || {}) } }); });
    byKey = merged;
  }
  const within = linksWithin(plan, byKey);
  for (let copy = 1; copy <= copies; copy += 1) {
    const map = new Map();       // original key → new key, this copy
    const failed = new Set();
    for (const node of plan) {
      /* eslint-disable no-await-in-loop */
      const it = byKey.get(node.key);
      if (node.parentKey && !map.has(node.parentKey)) {
        failed.add(node.key);
        errors.push({ copy, from: node.key, error: `its parent ${node.parentKey} was not cloned` });
        done += 1; onProgress({ done, total, created: created.length });
        continue;
      }
      try {
        const ownProject = (fieldsOf(it).project && fieldsOf(it).project.key) || String(node.key).split('-')[0];
        const projectKey = project || ownProject;
        let type = fieldsOf(it).issuetype || {};
        if (project && project !== ownProject && typeof typeIn === 'function') {
          const t = await typeIn(projectKey, type.name);
          if (!t) throw new Error(`${projectKey} has no work type "${type.name || '?'}"`);
          type = { id: t.id, name: t.name || type.name };
        }
        const meta = await metaOf(projectKey, type.id, type.name);
        const fields = cloneFieldsFor(it, meta, { clear, set: (edits && edits[node.key]) || null, summary: summaryOf ? summaryOf(it, copy) : null });
        fields.project = { key: projectKey };
        fields.issuetype = type.id ? { id: String(type.id) } : { name: type.name };
        const anchors = node.parentKey ? [] : (node.anchors || []);
        if (node.edge && node.edge.kind === 'parent') fields.parent = { key: map.get(node.parentKey) };
        const parentAnchor = anchors.find((a) => a && a.edge && a.edge.kind === 'parent' && a.key);
        if (parentAnchor) fields.parent = { key: parentAnchor.key };
        const made = await api.createIssue({ fields });
        const newKey = made && (made.key || (made.issue && made.issue.key));
        if (!newKey) throw new Error('the server did not name the new work item');
        map.set(node.key, newKey);
        created.push({ copy, from: node.key, key: newKey });
        for (const a of anchors) {
          if (!a || !a.edge || a.edge.kind !== 'link' || !a.key) continue;
          try {
            await api.linkIssue(a.edge.childIs === 'inward' ? linkPayload(a.edge.type, newKey, a.key) : linkPayload(a.edge.type, a.key, newKey));
          } catch (e) {
            errors.push({ copy, from: node.key, error: `made as ${newKey}, but could not be joined to ${a.key}: ${(e && e.message) || 'refused'}` });
          }
        }
        if (node.edge && node.edge.kind === 'link') {
          const parentNew = map.get(node.parentKey);
          await api.linkIssue(node.edge.childIs === 'inward'
            ? linkPayload(node.edge.type, newKey, parentNew)
            : linkPayload(node.edge.type, parentNew, newKey));
        } else if (node.parentKey && !node.edge) {
          errors.push({ copy, from: node.key, error: `made as ${newKey}, but the data does not say how it hangs off ${node.parentKey}, so it was not joined` });
        }
      } catch (e) {
        failed.add(node.key);
        const msg = (e && e.response && e.response.data && (
          (e.response.data.details && (e.response.data.details.errorMessages || []).join('; ')) || e.response.data.details || e.response.data.error
        )) || (e && e.message) || 'refused';
        errors.push({ copy, from: node.key, error: String(msg) });
      }
      done += 1;
      onProgress({ done, total, created: created.length });
      /* eslint-enable no-await-in-loop */
    }
    for (const l of within) {
      if (!map.has(l.from) || !map.has(l.to)) continue;
      /* eslint-disable no-await-in-loop */
      try { await api.linkIssue(linkPayload(l.type, map.get(l.to), map.get(l.from))); }
      catch (e) { errors.push({ copy, from: l.from, error: `the ${l.type.name} link to ${l.to} could not be remade: ${(e && e.message) || 'refused'}` }); }
      /* eslint-enable no-await-in-loop */
    }
  }
  return { created, errors, total };
}
